interface IssueTypeInfo {
    name: string;
    tts: string;
    phase: string;
}

interface RecordedIssue {
    type: string;
    position: number;
    severity: string;
    cornerNumber: number;
    notes: string;
    lap: number;
    timestamp: string;
}

interface RecordIssueOptions {
    severity?: string;
    cornerNumber?: number;
    notes?: string;
    announce?: boolean;
}

interface DetectionThresholds {
    brakeDiff?: number;
    throttleDiff?: number;
    speedDiff?: number;
    steeringDiff?: number;
    lineDiff?: number;
    [key: string]: number | undefined;
}

interface TelemetryPoint {
    brake?: number;
    throttle?: number;
    understeer?: number;
    oversteer?: number;
    wheelLockup?: boolean;
    wheelSpin?: number;
    offTrack?: boolean;
    trackLimitWarning?: boolean;
    [key: string]: unknown;
}

interface DetectedIssue {
    type: string;
    severity: string;
}

class CoachingTracker {
    apiBase: string;
    ttsAgent: TTSAgentLike | null;
    apiToken: string | null;
    currentTrackId: string | null;
    currentTrackName: string | null;
    currentLap: number;
    sessionIssues: RecordedIssue[];
    issueTypes: Record<string, IssueTypeInfo>;

    constructor(options: { apiBase?: string; ttsAgent?: TTSAgentLike; apiToken?: string } = {}) {
        this.apiBase = options.apiBase || '/api';
        // Fall back to global ttsAgent instance if available
        this.ttsAgent = options.ttsAgent ?? ((window as any).ttsAgent || null); // eslint-disable-line @typescript-eslint/no-explicit-any
        this.apiToken = options.apiToken || null;

        this.currentTrackId = null;
        this.currentTrackName = null;
        this.currentLap = 0;
        this.sessionIssues = [];

        this.issueTypes = {
            late_braking: { name: 'Late Braking', tts: 'brake earlier', phase: 'approach' },
            early_braking: { name: 'Early Braking', tts: 'brake later', phase: 'approach' },
            trail_brake: { name: 'Trail Braking', tts: 'trail brake more', phase: 'entry' },
            early_throttle: { name: 'Early Throttle', tts: 'wait for throttle', phase: 'exit' },
            late_throttle: { name: 'Late Throttle', tts: 'get on throttle earlier', phase: 'exit' },
            throttle_lift: { name: 'Throttle Lift', tts: 'stay on throttle', phase: 'exit' },
            missed_apex: { name: 'Missed Apex', tts: 'hit the apex', phase: 'apex' },
            early_apex: { name: 'Early Apex', tts: 'apex later', phase: 'apex' },
            late_apex: { name: 'Late Apex', tts: 'apex earlier', phase: 'apex' },
            understeer: { name: 'Understeer', tts: 'reduce entry speed', phase: 'entry' },
            oversteer: { name: 'Oversteer', tts: 'smooth inputs', phase: 'entry' },
            track_limits: { name: 'Track Limits', tts: 'stay on track', phase: 'exit' },
            gear_selection: { name: 'Wrong Gear', tts: 'check your gear', phase: 'apex' },
            inconsistent_line: { name: 'Inconsistent Line', tts: 'use consistent line', phase: 'entry' },
            rough_steering: { name: 'Rough Steering', tts: 'smooth steering', phase: 'entry' },
            spin: { name: 'Spin', tts: 'be careful here', phase: 'exit' },
            lockup: { name: 'Wheel Lockup', tts: 'lighter braking', phase: 'approach' },
            wheelspin: { name: 'Wheelspin', tts: 'gentle throttle', phase: 'exit' }
        };
    }

    getAuthHeaders(): Record<string, string> {
        const headers: Record<string, string> = { 'Content-Type': 'application/json' };
        if (this.apiToken) {
            headers['Authorization'] = `Bearer ${this.apiToken}`;
        }
        return headers;
    }

    setTrack(trackId: string, trackName: string = ''): void {
        this.currentTrackId = trackId;
        this.currentTrackName = trackName;
        this.currentLap = 0;
        this.sessionIssues = [];
    }

    recordIssue(type: string, position: number, options: RecordIssueOptions = {}): RecordedIssue {
        const issue: RecordedIssue = {
            type: type,
            position: position,
            severity: options.severity || 'medium',
            cornerNumber: options.cornerNumber || 0,
            notes: options.notes || '',
            lap: this.currentLap,
            timestamp: new Date().toISOString()
        };

        this.sessionIssues.push(issue);

        if (options.announce && this.ttsAgent) {
            const issueInfo = this.issueTypes[type];
            if (issueInfo) {
                this.ttsAgent.speak(issueInfo.name, 'low');
            }
        }

        return issue;
    }

    startLap(lapNumber: number): void {
        if (this.currentLap > 0 && this.sessionIssues.length > 0) {
            this.submitLapIssues();
        }

        this.currentLap = lapNumber;
        this.sessionIssues = [];
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    async submitLapIssues(): Promise<any> {
        if (!this.currentTrackId || this.sessionIssues.length === 0) {
            return { success: false, error: 'No issues to submit' };
        }

        try {
            const response = await fetch(`${this.apiBase}/coaching_tracker?action=record`, {
                method: 'POST',
                headers: this.getAuthHeaders(),
                body: JSON.stringify({
                    trackId: this.currentTrackId,
                    trackName: this.currentTrackName,
                    lapNumber: this.currentLap,
                    issues: this.sessionIssues
                })
            });

            const result = await response.json();

            if (result.success) {
                this.sessionIssues = [];
            }

            return result;
        } catch (error) {
            console.error('Failed to submit lap issues:', error);
            return { success: false, error: error instanceof Error ? error.message : String(error) };
        }
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    async getAnalysis(trackId: string | null = null): Promise<any> {
        const track = trackId || this.currentTrackId;
        if (!track) {
            return { success: false, error: 'No track specified' };
        }

        try {
            const response = await fetch(
                `${this.apiBase}/coaching_tracker?action=analysis&trackId=${encodeURIComponent(track)}`,
                { headers: this.getAuthHeaders() }
            );
            return await response.json();
        } catch (error) {
            console.error('Failed to get analysis:', error);
            return { success: false, error: error instanceof Error ? error.message : String(error) };
        }
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    async getTTSNotes(trackId: string | null = null): Promise<any> {
        const track = trackId || this.currentTrackId;
        if (!track) {
            return { success: false, error: 'No track specified' };
        }

        try {
            const response = await fetch(
                `${this.apiBase}/coaching_tracker?action=tts_notes&trackId=${encodeURIComponent(track)}`,
                { headers: this.getAuthHeaders() }
            );
            return await response.json();
        } catch (error) {
            console.error('Failed to get TTS notes:', error);
            return { success: false, error: error instanceof Error ? error.message : String(error) };
        }
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    async getHistory(trackId: string | null = null, options: { limit?: string; type?: string } = {}): Promise<any> {
        const track = trackId || this.currentTrackId;
        if (!track) {
            return { success: false, error: 'No track specified' };
        }

        const params = new URLSearchParams({
            action: 'history',
            trackId: track
        });

        if (options.limit) params.append('limit', options.limit);
        if (options.type) params.append('type', options.type);

        try {
            const response = await fetch(
                `${this.apiBase}/coaching_tracker?${params}`,
                { headers: this.getAuthHeaders() }
            );
            return await response.json();
        } catch (error) {
            console.error('Failed to get history:', error);
            return { success: false, error: error instanceof Error ? error.message : String(error) };
        }
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    async getTracks(): Promise<any> {
        try {
            const response = await fetch(
                `${this.apiBase}/coaching_tracker?action=tracks`,
                { headers: this.getAuthHeaders() }
            );
            return await response.json();
        } catch (error) {
            console.error('Failed to get tracks:', error);
            return { success: false, error: error instanceof Error ? error.message : String(error) };
        }
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    async clearTrack(trackId: string): Promise<any> {
        try {
            const response = await fetch(`${this.apiBase}/coaching_tracker?action=clear`, {
                method: 'POST',
                headers: this.getAuthHeaders(),
                body: JSON.stringify({ trackId })
            });
            return await response.json();
        } catch (error) {
            console.error('Failed to clear track:', error);
            return { success: false, error: error instanceof Error ? error.message : String(error) };
        }
    }

    getIssueTypes(): (IssueTypeInfo & { type: string })[] {
        return Object.entries(this.issueTypes).map(([type, info]) => ({
            type,
            ...info
        }));
    }

    detectIssues(current: TelemetryPoint, _reference: TelemetryPoint, thresholds: DetectionThresholds = {}): DetectedIssue[] {
        const issues: DetectedIssue[] = [];
        const _t = {
            brakeDiff: thresholds.brakeDiff || 0.1,
            throttleDiff: thresholds.throttleDiff || 0.1,
            speedDiff: thresholds.speedDiff || 5,
            steeringDiff: thresholds.steeringDiff || 10,
            lineDiff: thresholds.lineDiff || 2,
            ...thresholds
        };

        if ((current.brake ?? 0) > 0.5 && (_reference.brake ?? 0) < 0.1) {
            issues.push({ type: 'early_braking', severity: 'medium' });
        } else if ((current.brake ?? 0) < 0.1 && (_reference.brake ?? 0) > 0.5) {
            issues.push({ type: 'late_braking', severity: 'high' });
        }

        if ((current.throttle ?? 0) > 0.5 && (_reference.throttle ?? 0) < 0.1) {
            issues.push({ type: 'early_throttle', severity: 'medium' });
        } else if ((current.throttle ?? 0) < 0.3 && (_reference.throttle ?? 0) > 0.7) {
            issues.push({ type: 'late_throttle', severity: 'medium' });
        }

        if (current.understeer && current.understeer > 0.3) {
            issues.push({ type: 'understeer', severity: 'medium' });
        }
        if (current.oversteer && current.oversteer > 0.3) {
            issues.push({ type: 'oversteer', severity: 'high' });
        }

        if (current.wheelLockup) {
            issues.push({ type: 'lockup', severity: 'high' });
        }

        if (current.wheelSpin && current.wheelSpin > 0.1) {
            issues.push({ type: 'wheelspin', severity: 'medium' });
        }

        if (current.offTrack || current.trackLimitWarning) {
            issues.push({ type: 'track_limits', severity: 'medium' });
        }

        return issues;
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    generateSummary(analysis: any): string {
        if (!analysis || !analysis.analysis) {
            return "No coaching data available for this track.";
        }

        const a = analysis.analysis;
        const parts: string[] = [];

        if (a.multiLocationIssues && a.multiLocationIssues.length > 0) {
            const top = a.multiLocationIssues[0];
            parts.push(`Focus on ${top.name.toLowerCase()}, it's happening at ${top.locationCount} locations`);
        }

        if (a.patterns && a.patterns.length > 0) {
            const top = a.patterns[0];
            const issueInfo = this.issueTypes[top.type];
            if (issueInfo && top.cornerNumber > 0) {
                parts.push(`Watch turn ${top.cornerNumber}, ${issueInfo.tts}`);
            }
        }

        if (a.stats) {
            parts.push(`Analyzed ${a.stats.lapsAnalyzed} laps with ${a.stats.patternCount} patterns`);
        }

        return parts.join('. ') || "Keep driving to build coaching data.";
    }
}

window.CoachingTracker = CoachingTracker;
